import { t } from "../i18n";
import {
  AlertTriangle,
  Check,
  LoaderCircle,
  Trash2,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { api } from "../api";
import { ConfirmDangerDialog } from "../components/shared/ConfirmDangerDialog";
import { SettingsPage } from "./settings/ProfilePage";

export function DataImportPage({
  onImported,
}: {
  onImported: () => void | Promise<void>;
}) {
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const importOpenWebUI = async (file?: File) => {
    if (!file) return;
    setImporting(true);
    setNotice("");
    setError("");
    try {
      const payload = JSON.parse(await file.text());
      const result = await api<{ imported: number }>(
        "/conversations/import/openwebui",
        { method: "POST", body: JSON.stringify(payload) },
      );
      setNotice(
        `Imported ${result.imported} ${result.imported === 1 ? "conversation" : "conversations"}.`,
      );
      await onImported();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not import the OpenWebUI export",
      );
    } finally {
      setImporting(false);
    }
  };

  const deleteAll = async () => {
    setDeleting(true);
    setNotice("");
    setError("");
    try {
      const result = await api<{ deleted: number }>("/conversations/all", {
        method: "DELETE",
      });
      setNotice(
        `Deleted ${result.deleted} ${result.deleted === 1 ? "conversation" : "conversations"}.`,
      );
      setConfirmingDelete(false);
      await onImported();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not delete conversations",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SettingsPage
      title={t("Data")}
      description="Bring conversation history from another chat workspace into your Lumen account."
    >
      <section className="panel mb-5 max-w-2xl p-5 sm:p-6">
        <h3 className="text-lg font-bold">{t("OpenWebUI")}</h3>
        <p className="mt-2 text-sm leading-6 text-stone-500">
          {t(
            "Import a single-chat or all-chats JSON export. Titles, timestamps, model metadata, edited responses, and the selected conversation branch are preserved.",
          )}
        </p>
        <div className="mt-5">
          <label
            className={`button-primary cursor-pointer ${importing ? "pointer-events-none opacity-60" : ""}`}
          >
            {importing ? (
              <LoaderCircle className="animate-spin" size={17} />
            ) : (
              <Upload size={17} />
            )}
            {importing ? "Importing…" : "Choose OpenWebUI export"}
            <input
              type="file"
              accept=".json,application/json"
              className="sr-only"
              disabled={importing}
              onChange={(event) => {
                void importOpenWebUI(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
          </label>
        </div>
        {notice && (
          <p className="mt-4 flex items-center gap-2 text-sm text-lumen-700 dark:text-lumen-300">
            <Check size={16} />
            {notice}
          </p>
        )}
        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-300">{error}</p>
        )}
      </section>
      <section className="max-w-2xl rounded-2xl border border-red-200 bg-red-50/60 p-5 sm:p-6 dark:border-red-900/70 dark:bg-red-950/20">
        <div className="flex items-start gap-3">
          <AlertTriangle
            className="mt-0.5 shrink-0 text-red-600 dark:text-red-300"
            size={20}
          />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">
              {t("Delete all conversations")}
            </h3>
            <p className="mt-1 text-sm leading-6 text-red-700/80 dark:text-red-300/80">
              {t(
                "This permanently deletes every conversation and message in your account, including conversations that were not imported. This cannot be undone.",
              )}
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            className="button bg-red-600 text-white hover:bg-red-700"
            disabled={deleting || importing}
            onClick={() => setConfirmingDelete(true)}
          >
            <Trash2 size={16} /> {t("Delete all conversations")}
          </button>
        </div>
      </section>
      {confirmingDelete && (
        <ConfirmDangerDialog
          title={t("Delete every conversation?")}
          description="All conversations and their messages in your account will be permanently deleted. Imported and manually created conversations are both included, and this action cannot be undone."
          confirmLabel="Delete everything"
          busy={deleting}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => void deleteAll()}
        />
      )}
    </SettingsPage>
  );
}
